import type { ModuleSnapshot } from '@ctcells/contract'

import type { GantryStop, StationPhase } from '../../figures/LineFigures.tsx'

import { stationPhaseOf } from './processSteps.ts'

export type { GantryStop }

/**
 * **어셈블러 정면도의 «지금»** — 3축 갠트리가 어디에 있고(`stop`) 내려가 있는지(`down`) · TC 를 쥐었는지(`holding`).
 * (eric 2026-10-08 「어셈블러에서.. 3축 로봇.. 여기서는.. tc rack 을 놓고 빼는 거 보여줘」)
 *
 * 🔴 서버 사실 셋으로만 고른다 — 좌표는 안 오므로 자리는 도식이다(염색기 툴 · 6축과 같은 규칙).
 * | 사실 | 출처 |
 * | --- | --- |
 * | 상태 `TC_LOAD` · `UV` · `TC_REMOVE` | `ModuleSnapshot.state` → `stationPhaseOf` |
 * | 쥠 | `grips[].state === 'holding'`(dev2 `assembler-1` 의 `asm.cart-1` 실측 2026-10-08) |
 * | 동작 이름 `grip` · `release` | `current_execution.sequence`(module-core `assembler.*` 원자 시퀀스 14개) |
 *
 * | 상태 | 빈손 | 쥠 |
 * | --- | --- | --- |
 * | `load`(TC 장착) | TC 랙 위 — 집으러 | 스테이지 위 — 얹으러 |
 * | `uv` | 떨어진 자리 · 올라간 채(누름판이 누른다) | — |
 * | `remove`(TC 분리) | 스테이지 위 — 집으러 | 폐기통 위 — 버리러(레시피 `placedown waste_container`) |
 * | 그 밖 · 끊긴 화면 | `undefined` — 그림이 스테이지에 둔다 | |
 *
 * 내려감 — 동작이 `grip` · `release` 면 내려간다. UV 중은 올라간 채(누르는 건 압착 장치다). 동작 이름이 없으면(실행 요약 없음) 종전 규칙대로 `idle` 아니면 내려옴.
 * 🔴 다음 동작을 짐작하거나 시간을 재서 움직이지 않는다 — 프레임이 바뀌면 그때 자리가 바뀐다.
 */
export interface AsmStationView {
  phase: StationPhase
  stop: GantryStop | undefined
  down: boolean
  holding: boolean
}

const GRIP_SEQUENCES: ReadonlySet<string> = new Set(['grip', 'release'])

export function asmGripping(snapshot: ModuleSnapshot | undefined): boolean {
  return (snapshot?.grips ?? []).some((grip) => grip.state === 'holding')
}

export function asmStationView(
  snapshot: ModuleSnapshot | undefined,
  stale: boolean,
): AsmStationView {
  const phase = stationPhaseOf(snapshot?.state, stale)
  if (phase === 'idle') return { phase, stop: undefined, down: false, holding: false }
  const holding = asmGripping(snapshot)
  const sequence = snapshot?.current_execution?.sequence ?? undefined
  const stop: GantryStop =
    phase === 'load'
      ? holding
        ? 'stage'
        : 'rack'
      : phase === 'remove' && holding
        ? 'waste'
        : 'stage'
  /* UV 는 압착 장치가 누르는 구간 — 그리퍼는 올라간 채 떨어져 있다(2026-10-08 오후 · 누름판 분리). 동작 이름이 없으면 종전 규칙대로 내려옴 */
  const down = phase !== 'uv' && (sequence === undefined ? true : GRIP_SEQUENCES.has(sequence))
  return { phase, stop, down, holding }
}
